import { addDoc, collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/OrderGenerateReceipt.css';
import { auth, db } from '../firebase-config';
import Swal from "sweetalert2";

const OrderGenerateReceipt = () => {
  const [date, setDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState("");        // MM/DD/YYYY for display/saving
  const [rawDeliveryDate, setRawDeliveryDate] = useState("");
  const [branch, setBranch] = useState('');
  const [items, setItems] = useState([{ item: '', quantity: '', measurement: '', price: '', totalItemPrice: ''}]);
  const [ingredientsItems, setIngredientsItems] = useState([{ item: '', quantity: '', measurement: '', price: '', totalIngredientPrice: ''}]);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]); // State to store products list
  const [error, setError] = useState(null);
  const [dateTime, setDateTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  

  useEffect(() => {
    const today = new Date();
    
    // Get the local timezone offset in minutes
    const offset = today.getTimezoneOffset();
    
    // Adjust the time for local timezone
    const localDate = new Date(today.getTime() - offset * 60000); // Correcting for the time zone offset
    
    // Format the date and time to 'YYYY-MM-DDTHH:mm'
    const formattedDateTime = localDate.toISOString().slice(0, 16);
    setDateTime(formattedDateTime);
  }, []); // Only run once when the component mounts

  // Fetch products and user email on component mount
  useEffect(() => {
    const fetchProductsAndUser = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
              // Sort products by name (A-Z), case-insensitive
        const sortedProducts = productsData.sort((a, b) => {
          const nameA = a.name.toLowerCase(); // Normalize to lowercase for comparison
          const nameB = b.name.toLowerCase(); // Normalize to lowercase for comparison

          if (nameA < nameB) return -1; // A comes before B
          if (nameA > nameB) return 1;  // B comes before A
          return 0;  // names are equal
        });

        setProducts(sortedProducts); // Set sorted products
      } catch (err) {
        setError('Failed to fetch products. Please try again later.');
      }
  
      // Format date and time with AM/PM
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true, // AM/PM format
      });
  
      setDate(formattedDate); // Set formatted date with time and AM/PM
  
      const user = auth.currentUser;
      setLastUpdatedBy(user?.email || 'No user logged in');
    };
  
    fetchProductsAndUser();
  }, []);

  const formatToMMDDYYYY = (isoDate) => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split("-");
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "ingredients"));
        const ingredientsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
  
        const sortedIngredients = ingredientsData.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
  
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
  
        setIngredients(sortedIngredients);
      } catch (error) {
        console.error("Error fetching ingredients: ", error);
      }
    };
  
    fetchIngredients();
  }, []); // Run only once when the component mounts

  // Function to add a log entry
  const addLogEntry = async (itemData) => {
    try {
      const currentDate = new Date();
  
      // Format current date and time with AM/PM
      const formattedDate = currentDate.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true, // AM/PM format
      });
  
      await addDoc(collection(db, "item_logs"), {
        itemName: itemData.name,
        typeOfChange: 'Order',
        lastUpdatedBy: lastUpdatedBy,
        date: formattedDate, // Use the formatted date with time
        descriptionOfChange: 'Removed',
        quantity: parseFloat(itemData.quantity),
        editChangeLog: '-',
      });
    } catch (error) {
      console.error('Error adding log:', error);
    }
  };
  

  const handleIngredientChange = (index, e) => {
    const values = [...ingredientsItems];
    values[index][e.target.name] = e.target.value;
    
    if (e.target.name === 'item') {
      const selectedIngredient = ingredients.find(ingredients => ingredients.name === e.target.value);
      if (selectedIngredient) {
        values[index].price = selectedIngredient.sellerPrice || '';
        values[index].measurement = selectedIngredient.measurement;
      }
    }
  
    setIngredientsItems(values);
  
    // Log the change
    console.log('Updated ingredient:', { ...values[index] });
  };

  // Handle change in the fields
  const handleInputChange = (index, e) => {
    const values = [...items];
    values[index][e.target.name] = e.target.value;

    // If an item is selected, pre-fill the price based on product
    if (e.target.name === 'item') {
      const selectedProduct = products.find(product => product.name === e.target.value);
      if (selectedProduct) {
        values[index].price = selectedProduct.sellerPrice || '';
        values[index].measurement = selectedProduct.measurement;
      }
    }

    setItems(values);
  };

  // Add a new item row
  const addItemRow = () => {
    setItems([...items, { item: '', quantity: '', measurement: '', price: '', totalItemPrice: ''}]);
  };

  // Remove an item row
  const removeItemRow = (index) => {
    const values = [...items];
    values.splice(index, 1);
    setItems(values);
  };

  const addIngredientRow = () => {
    setIngredientsItems([...ingredientsItems, { item: '', quantity: '', measurement: '', price: '', totalIngredientPrice: '' }]);
  };

  const removeIngredientRow = (index) => {
    const values = [...ingredientsItems];
    values.splice(index, 1);
    setIngredientsItems(values);
  };

  const calculateXOffset = (textWidth) => {
    return 77 + (textWidth / 2);
  };

  const shippingFee = (parseFloat(items[0]?.shippingFee) || 0).toFixed(2);
  const shippingFeeValue = parseFloat(shippingFee);


  // Calculate the total amount (sum of quantity * price for all items)
  const subtotal = (
    [...items, ...ingredientsItems].reduce(
      (total, item) =>
        total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
      0
    )
  ).toFixed(2);

  const totalAmount = (
    [...items, ...ingredientsItems].reduce(
      (total, item) =>
        total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
      0
    ) + shippingFeeValue
  ).toFixed(2);

  const handleGenerateReceiptExcel = () => {
    toast.success("Payment Receipt Excel will now be downloaded", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      progress: undefined,
    });
  
    // 3. Create a summary sheet
    const summarySheetData = [
      [" "],
      ["Branch Name", branch],
      ["Delivery Date", deliveryDate],
      ["Date Created", date],
      ["User", lastUpdatedBy.split('@')[0]],
      [" "],
      ["PRODUCTS/INGREDIENTS", "QUANTITY", "PRICE", "TOTAL"],
    ];

    ingredientsItems.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
          'PHP ' + item.price,
          'PHP ' + item.totalIngredientPrice
        ]);
      }
    });

    items.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
          'PHP ' + item.price,
          'PHP ' + item.totalItemPrice
        ]);
      }
    });

    summarySheetData.push([""])
    summarySheetData.push(["", "", "Subtotal", `PHP ${subtotal}`])
    summarySheetData.push(["", "", "Shipping Fee", `PHP ${shippingFee}`])
    summarySheetData.push(["", "", "Final Total Price", `PHP ${totalAmount}`])
  
    // 4. Convert to Excel Sheets
    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.sheet_add_aoa(summarySheet, [["Dimsum Treats", " ", " ", " "]], { origin: "A1" });

    summarySheet["!cols"] = [
      { wch: 40 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 }
    ];
  
    // 5. Create Workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  
    // 6. Save Excel File
    XLSX.writeFile(workbook, 'payment_receipt.xlsx');
  };
  
  const handleGenerateDeliveryExcel = () => {
    toast.success("Delivery Receipt Excel will now be downloaded", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      progress: undefined,
    });
  
    // 3. Create a summary sheet
    const summarySheetData = [
      [" "],
      ["Branch Name", branch],
      ["Delivery Date", deliveryDate],
      ["User", lastUpdatedBy.split('@')[0]],
      [" "],
      ["PRODUCTS/INGREDIENTS", "QUANTITY"],
    ];

    ingredientsItems.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
        ]);
      }
    });

    items.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
        ]);
      }
    });
  
    // 4. Convert to Excel Sheets
    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.sheet_add_aoa(summarySheet, [["Dimsum Treats", " "]], { origin: "A1" });

    summarySheet["!cols"] = [
      { wch: 40 },
      { wch: 20 },
    ];
  
    // 5. Create Workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  
    // 6. Save Excel File
    XLSX.writeFile(workbook, 'delivery_receipt.xlsx');
  };

  // Update product quantity in Firestore
  const updateProductQuantity = async (productId, quantitySold) => {
    const productRef = doc(db, 'products', productId);
    try {
      const productDoc = await getDoc(productRef);
      const productData = productDoc.data();
  
      if (productData && productData.quantity >= quantitySold) {
        const newQuantity = productData.quantity - quantitySold;
        await updateDoc(productRef, {
          quantity: newQuantity,
        });
        console.log(`Updated product ${productId}: New quantity is ${newQuantity}`);
      } else {
        toast.warning("Not enough stock to complete this order.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (err) {
      console.error("Error updating product quantity:", err);
    }
  };

  const updateIngredientQuantity = async (ingredientId, quantitySold) => {
    const ingredientRef = doc(db, 'ingredients', ingredientId);
    try {
      const ingredientDoc = await getDoc(ingredientRef);
      const ingredientData = ingredientDoc.data();
  
      if (ingredientData && ingredientData.quantity >= quantitySold) {
        const newQuantity = ingredientData.quantity - quantitySold;
        await updateDoc(ingredientRef, {
          quantity: newQuantity,
        });
        console.log(`Updated ingredient ${ingredientId}: New quantity is ${newQuantity}`);
      } else {
        toast.warning("Not enough stock to complete this order.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (err) {
      console.error("Error updating ingredient quantity:", err);
    }
  };

  const checkSufficientQuantity = () => {
    const insufficientItems = [];

    // Check products
    for (let item of items) {
      const product = products.find(p => p.name === item.item);
      if (product && parseFloat(item.quantity) > product.quantity) {
        insufficientItems.push({ name: item.item, availableQuantity: product.quantity });
      }
    }

    // Check ingredients
    for (let ingredient of ingredientsItems) {
     const ingredientItem = ingredients.find(i => i.name === ingredient.item);
      if (ingredientItem && parseFloat(ingredient.quantity) > ingredientItem.quantity) {
        insufficientItems.push({ name: ingredient.item, availableQuantity: ingredientItem.quantity });
     }
    }

    if (insufficientItems.length > 0) {
      let errorMessage = 'The following items lack sufficient quantity: ';
      insufficientItems.forEach(item => {
        errorMessage += `${item.name}: ${item.availableQuantity} available `;
      });
      toast.warning(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
      return true; // Return true to indicate an error occurred
    }
    return false; // No errors found
  };

  const handleDateTimeChange = (e) => {
    setDateTime(e.target.value);
  };

  const resetForm = () => {
    setBranch('');
    setItems([{ item: '', quantity: '', measurement: '', price: '', totalItemPrice: '' }]);
    setIngredientsItems([{ item: '', quantity: '', measurement: '', price: '', totalIngredientsPrice: '' }]);
  };

  // Save the entries to Firestore and update product quantities
  const handleSaveEntries = async (event) => {
    event.preventDefault();

    // Check if branch and date are filled
    if (!branch || !date) {
        toast.error("Please fill in all required fields (Branch Name and Date).", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        });
        return;
    
    }

    // Check if all items have sufficient quantity
    if (checkSufficientQuantity()) {
      return; // Prevent form submission if items are insufficient
    }

    // Check if all items have a valid selection, quantity, and price
    // const invalidItems = items.filter(item => !item.item || !item.quantity || !item.price);
    // if (invalidItems.length > 0) {
    //     toast.error("Please fill in all fields for each item.", {
    //       position: "top-right",
    //       autoClose: 5000,
    //       hideProgressBar: false,
    //       closeOnClick: true,
    //       pauseOnHover: false,
    //       draggable: true,
    //       progress: undefined,
    //     });
    //     return;
     
    // }

    // const invalidIngredients = ingredientsItems.filter(ingredients => !ingredients.item || !ingredients.quantity || !ingredients.price);
    // if (invalidIngredients.length > 0) {
    //     toast.error("Please fill in all fields for each product.", {
    //       position: "top-right",
    //       autoClose: 5000,
    //       hideProgressBar: false,
    //       closeOnClick: true,
    //       pauseOnHover: false,
    //       draggable: true,
    //       progress: undefined,
    //     });
    //     return;
    // }

    const insufficientItems = checkSufficientQuantity();
    if (insufficientItems.length > 0) {
      let itemsLack = 'The following items lack sufficient quantity:';
      insufficientItems.forEach(item => {
        itemsLack += `${item.name}: ${item.availableQuantity} available`;
      });
      toast.warning(itemsLack, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
      return;
    }

       const confirmSubmit = await Swal.fire({
          title: 'Confirm Submission',
          text: "You are about to submit this receipt. Make sure that the details are correct",
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        })

        if (confirmSubmit.isConfirmed) {
          try {
            setIsLoading(true); // Set loading state to true
      
            const currentDate = new Date();
      
            const formattedDate = [
              currentDate.getMonth() + 1,
              currentDate.getDate(),
              currentDate.getFullYear()
            ].join('/') + ' ' +
            [currentDate.getHours(), currentDate.getMinutes()].join(':')||"";
      
            const orderReceiptsRef = collection(db, 'orderReceipts');

            const receiptData = {
              branch,
              deliveryDate,
              date: formattedDate,
              items,
              ingredients: ingredientsItems,
              subtotal,
              shippingFee,
              totalAmount,
              lastUpdatedBy,
            };
        
            // Add the order receipt to Firestore
            const docRef = await addDoc(orderReceiptsRef, receiptData);
        
            // Update the stock quantity for each product in the order
            for (let item of items) {
              const product = products.find(p => p.name === item.item);
              if (product && item.quantity) {
                  addLogEntry({
                    name: item.item,
                    quantity: item.quantity,
                  });
                await updateProductQuantity(product.id, parseFloat(item.quantity));  // Make sure quantity is an integer
              }
            }

        
      
            for (const ingredient of ingredientsItems) {
              const ingredientItem = ingredients.find(i => i.name === ingredient.item);
              if (ingredientItem && ingredient.quantity) {

                  addLogEntry({
                    name: ingredient.item,
                    quantity: ingredient.quantity,
                  });

                await updateIngredientQuantity(ingredientItem.id, parseFloat(ingredient.quantity));
              }
            }

      
        
            toast.success("Receipt saved and product quantities updated successfully!", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: true,
              progress: undefined,
            });
      
            // Create log entries for all items in the order

      

      
            resetForm();
          } catch (err) {
            setError('Error saving entries. Please try again.');
            console.error('Error saving entries:', err);
          } finally {
            setIsLoading(false); // Reset loading state
          }
        }
};

  if (error) return <div>Error: {error}</div>;

  return (
    <form onSubmit={handleSaveEntries}>
      <h1>Generate Receipt</h1><br /><br />

      {/* Branch Name and Date Fields in One Row */}
      <div className="form-row branch-date-row">
        <div className="form-field">
          <label>Branch Name:</label>
          <input
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label>Delivery Date:</label>
          <input
            type="date"
            value={rawDeliveryDate} // must be YYYY-MM-DD
            onChange={(e) => {
              const isoDate = e.target.value;
              setRawDeliveryDate(isoDate);                 // for date picker binding
              setDeliveryDate(formatToMMDDYYYY(isoDate));  // for display/save as MM/DD/YYYY
            }}
            required
          />
        </div>

        <div className="form-field">
          <label>Date Created:</label>
          <input
            type="datetime-local"
            id="date"
            name="date"
            value={dateTime}
            onChange={handleDateTimeChange} // Update state when user selects a date
          />
        </div>
      </div>

      {ingredientsItems.map((ingredient, index) => (
      <div key={index} className="form-row">
        <div className="form-field">
          <label>Ingredients:</label>
          <select
            name="item"
            value={ingredient.item}
            onChange={(e) => handleIngredientChange(index, e)}
          >
            <option value="">Select Item</option>
            {ingredients.map((ingredient, idx) => (
              <option key={idx} value={ingredient.name}>
                {ingredient.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Quantity:</label>
          <input
            type="number"
            name="quantity"
            value={ingredient.quantity}
            onChange={(e) => handleIngredientChange(index, e)}
          />
      </div>

      <div className="form-field">
            <label>Unit:</label>
            <input
              type="text"
              name="measurement"
              value={ingredient.measurement}
              onChange={(e) => handleIngredientChange(index, e)}
              disabled
            />
          </div>

        <div className="form-field">
          <label>Price:</label>
          <input
            type="number"
            name="price"
            value={ingredient.price}
            onChange={(e) => handleIngredientChange(index, e)}
          />
        </div>

        <div className="form-field">
            <label>Total Price:</label>
            <input
              type="number"
              name="total price"
              value={ingredient.totalIngredientPrice = ingredient.quantity * ingredient.price}
              onChange={(e) => handleInputChange(index, e)} 
              disabled
            />
          </div>

        {/* Remove Button */}
        <div className="remove-item">
          <button
            type="button"
            onClick={() => removeIngredientRow(index)}
            className="remove-button"
            disabled={ingredientsItems.length === 1}
          >
            Remove Item
          </button>
        </div>
      </div>
    ))}

    <button type="button" className="add-another-item-supplier" onClick={addIngredientRow}>
      Add Another Item
    </button>

      {/* Loop over the items array to generate item rows dynamically */}
      {items.map((item, index) => (
        <div key={index} className="form-row">
          <div className="form-field">
            <label>Products:</label>
            <select
              name="item"
              value={item.item}
              onChange={(e) => handleInputChange(index, e)}
              required
            >
              <option value="">Select Item</option>
              {products.map((product) => (
                <option key={product.id} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Quantity:</label>
            <input
              type="number"
              name="quantity"
              value={item.quantity}
              onChange={(e) => handleInputChange(index, e)}
              required
            />
          </div>

          <div className="form-field">
            <label>Unit:</label>
            <input
              type="text"
              name="measurement"
              value={item.measurement}
              onChange={(e) => handleInputChange(index, e)}
              disabled
            />
          </div>

          <div className="form-field">
            <label>Price:</label>
            <input
              type="number"
              name="price"
              value={item.price}
              onChange={(e) => handleInputChange(index, e)} 
              required
            />
          </div>

          <div className="form-field">
            <label>Total Price:</label>
            <input
              type="number"
              name="total price"
              value={item.totalItemPrice = item.quantity * item.price}
              onChange={(e) => handleInputChange(index, e)} 
              disabled
            />
          </div>

          {/* Remove Button */}
          <div className="remove-item">
            <button
              type="button"
              onClick={() => removeItemRow(index)}
              className="remove-btn"
              disabled={items.length === 1} // Disable remove button if only one item
            >
              Remove Item
            </button>
          </div>
        </div>
      ))}


      {/* Button to add another item row */}
      <button type="button" className="add-another-item-order" onClick={addItemRow}>
        Add Another Item
      </button>

      <div className="form-row">
        <div className="form-field">
            <label>Shipping Fee:</label>
            <input
                type="number"
                name="shippingFee"
                value={items[0]?.shippingFee || ''}
                onChange={(e) => {
                    const updatedItems = [...items];
                    updatedItems[0] = {...updatedItems[0], shippingFee: e.target.value};
                    setItems(updatedItems);
                }}
                required
              />
          </div>
      </div>

      {/* Action buttons for PDF and save */}
      <div className="action-buttons">
        <button type="button" className="generate-pdf-btn" onClick={handleGenerateReceiptExcel}>
          Print Payment Receipt
        </button>
        <button type="button" className="generate-pdf-btn" onClick={handleGenerateDeliveryExcel}>
          Print Delivery Receipt
        </button>
      </div>

        <button type="button" className="save-entries-btn" disabled={isLoading} onClick={handleSaveEntries}>
          {isLoading ? 'Submitting...' : 'Submit'}
        </button>

      {/* Total Amount */}
      <div className="overalltotal-container">
        <div className="totalshipping-container">
          <span className="total-label">Subtotal: ₱</span>
          <span className="total-value">{subtotal}</span>
        </div>
        <div className="totalshipping-container">
            <span className="total-label">Shipping Fee: ₱</span>
            <span className="total-value">{shippingFee}</span>
        </div>
        <div className="totalshipping-container">
            <span className="total-label">Total Amount: ₱</span>
            <span className="total-value">{totalAmount}</span>
        </div>
      </div>


      {/* Display logged-in user's email */}
      <div className="user-email">
        <span>Email: {lastUpdatedBy}</span>
      </div>

    </form>
  );
};

export default OrderGenerateReceipt;